import { useCallback, useMemo, useRef, useState } from 'react';

const defaultIsEqual = (a, b) => {
  if (Object.is(a, b)) {
    return true;
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (error) {
    return false;
  }
};

const clampLimit = value => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 50;
  }
  return Math.floor(parsed);
};

export const useBoardHistory = ({
  initialPresent = null,
  limit = 50,
  isEqual = defaultIsEqual,
} = {}) => {
  const maxEntries = clampLimit(limit);
  const transactionStartRef = useRef(null);

  const [history, setHistory] = useState(() => ({
    past: [],
    present: initialPresent,
    future: [],
  }));

  const reset = useCallback(nextPresent => {
    transactionStartRef.current = null;
    setHistory({
      past: [],
      present: nextPresent,
      future: [],
    });
  }, []);

  const record = useCallback(
    nextPresent => {
      transactionStartRef.current = null;
      setHistory(prev => {
        if (isEqual(prev.present, nextPresent)) {
          return prev;
        }
        const nextPast = [...prev.past, prev.present].slice(-maxEntries);
        return {
          past: nextPast,
          present: nextPresent,
          future: [],
        };
      });
    },
    [isEqual, maxEntries],
  );

  const beginTransaction = useCallback(snapshot => {
    transactionStartRef.current = snapshot;
  }, []);

  const commitTransaction = useCallback(
    nextPresent => {
      setHistory(prev => {
        const startSnapshot =
          transactionStartRef.current !== null
            ? transactionStartRef.current
            : prev.present;
        transactionStartRef.current = null;
        if (isEqual(startSnapshot, nextPresent)) {
          if (isEqual(prev.present, nextPresent)) {
            return prev;
          }
          return {
            ...prev,
            present: nextPresent,
          };
        }
        const nextPast = [...prev.past, startSnapshot].slice(-maxEntries);
        return {
          past: nextPast,
          present: nextPresent,
          future: [],
        };
      });
    },
    [isEqual, maxEntries],
  );

  const cancelTransaction = useCallback(() => {
    transactionStartRef.current = null;
  }, []);

  const undo = useCallback(() => {
    let nextPresent = null;
    setHistory(prev => {
      if (prev.past.length === 0) {
        nextPresent = null;
        return prev;
      }
      const previous = prev.past[prev.past.length - 1];
      nextPresent = previous;
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [prev.present, ...prev.future].slice(0, maxEntries),
      };
    });
    return nextPresent;
  }, [maxEntries]);

  const redo = useCallback(() => {
    let nextPresent = null;
    setHistory(prev => {
      if (prev.future.length === 0) {
        nextPresent = null;
        return prev;
      }
      const [next, ...remainingFuture] = prev.future;
      nextPresent = next;
      return {
        past: [...prev.past, prev.present].slice(-maxEntries),
        present: next,
        future: remainingFuture,
      };
    });
    return nextPresent;
  }, [maxEntries]);

  return useMemo(
    () => ({
      past: history.past,
      present: history.present,
      future: history.future,
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
      beginTransaction,
      commitTransaction,
      cancelTransaction,
      record,
      reset,
      undo,
      redo,
    }),
    [
      beginTransaction,
      commitTransaction,
      cancelTransaction,
      history.future,
      history.past,
      history.present,
      record,
      redo,
      reset,
      undo,
    ],
  );
};

